<template>
  <div class="fb-post-snippet">
    <div class="fb-post-snippet__caption-textarea-wrap">
      <slot name="textarea" />
    </div>
    <div v-if="$slots.locations" class="fb-post-snippet__locations">
      <slot name="locations" />
    </div>
    <!--<div class="fb-post-snippet__screen-size">{{ screenSize }}</div>-->

    <!-- Single photo: crop taller-than-4:5 to match Meta sync -->
    <div class="fb-post-snippet__single-photo-media" v-if="props.imagesList.length === 1" :title="getReasonForMedia(props.imagesList[0]) || undefined">
      <MediaThumbnail
        :mediaUrl="props.imagesList[0]"
        :lightboxKey="'FbPostPreview-' + props.lightboxKey"
        imageObjectFit="contain"
        :imageMaxHeight="singlePhotoImageMaxHeight"
        :minAspectRatio="singlePhotoMinAspectRatio"
      />
    </div>

    <!--
      Facebook collage recipes (P=portrait, L=landscape, S=square):

      2 photos:
      - P/P: two 4:5 columns (crop top/bottom rather than sides)
      - L/L: two wide rows
      - S/S or mixed orientations: two square columns

      3 photos:
      - First P: large photo left, two stacked right
      - First L or S: large photo top, two photos below

      4 photos:
      - First P: large photo left, three stacked right
      - First L: large photo top, three photos below
      - First S: 2x2 square grid

      For 3–4 photos, the first image determines the layout.
      5+ photos retain the existing two-over-three layout and +N overlay.
    -->
    <ul
      v-else
      :class="{
        'photos-grid': true,
        'photos-grid--1-photo-layout': props.imagesList.length === 1,
        'photos-grid--2-photos-layout': props.imagesList.length === 2,
        'photos-grid--3-photos-layout': props.imagesList.length === 3,
        'photos-grid--4-photos-layout': props.imagesList.length === 4,
        'photos-grid--5-photos-layout': props.imagesList.length >= 5,
        'photos-grid--primary-portrait': primaryPhotoOrientation === 'portrait',
        'photos-grid--primary-landscape': primaryPhotoOrientation === 'landscape',
        'photos-grid--primary-square': primaryPhotoOrientation === 'square',
        'photos-grid--mixed-two': hasMixedTwoPhotoOrientations,
        'fb-post-snippet__photos-grid': true,
      }"
    >
      <li class="photos-grid__photo-wrap" v-for="(mediaUrl, index) in props.imagesList" :key="index" :title="getReasonForMedia(mediaUrl) || null">
        <div v-if="props.imagesList.length > 5 && index === 4" class="photos-grid__counter-overlay">+{{ props.imagesList.length - 5 }}</div>
        <MediaThumbnail
          :class="{
            'photos-grid__photo': true,
            'photos-grid__photo--hidden': index >= 5,
          }"
          :mediaUrl="mediaUrl"
          :lightboxKey="'FbPostPreview-' + props.lightboxKey"
        />
      </li>
    </ul>
    <!-- / Photos grid -->
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, watch, Ref } from "vue";

// Components
import MediaThumbnail from "@components/MediaThumbnail.vue";

// Types
import { ScreenSize } from "@contracts/screenSize";
import { ImageSelectAIDebugData, SocialPostPublishType } from "@contracts/shareStop";
import { META_FEED_MIN_ASPECT_RATIO } from "@logic/AtlasSchedulingValidation";
import { getSingleSocialPreviewMediaMaxHeight } from "@helpers/SocialPostUIHelper";
import { getRemoteImageDimensions } from "@helpers/GetImageOrVideoResolution";
import FileUtils from "@logic/FileUtils";

type PrimaryPhotoOrientation = "portrait" | "landscape" | "square";

const props = withDefaults(
  defineProps<{
    imagesList: Array<string>;
    lightboxKey: string;
    debugData?: ImageSelectAIDebugData | null;
    postType?: SocialPostPublishType | null;
    cropTallPhotos?: boolean;
  }>(),
  {
    imagesList: () => [],
    lightboxKey: "",
    debugData: null,
    postType: null,
    cropTallPhotos: true,
  }
);

const screenSize = inject("screenSize") as Ref<ScreenSize>;

const singlePhotoImageMaxHeight = computed(() => getSingleSocialPreviewMediaMaxHeight(screenSize.value));
// Match Meta sync: crop single photos taller than 4:5 (0.81). Off when the post uses padding instead.
const singlePhotoMinAspectRatio = computed(() => (props.cropTallPhotos ? META_FEED_MIN_ASPECT_RATIO : null));

const primaryPhotoOrientation = ref<PrimaryPhotoOrientation | null>(null);
const hasMixedTwoPhotoOrientations = ref(false);
let orientationLoadSequence = 0;

const orientationWatchKey = computed(() => `${props.imagesList.length}|${props.imagesList[0] ?? ""}|${props.imagesList[1] ?? ""}`);

function classifyPhotoOrientation(width: number, height: number): PrimaryPhotoOrientation {
  if (width < height) return "portrait";
  if (width > height) return "landscape";
  return "square";
}

async function loadPhotoOrientation(mediaUrl: string): Promise<PrimaryPhotoOrientation> {
  const { w, h } = await getRemoteImageDimensions(FileUtils.getAdventureImagePath(mediaUrl, true));
  return classifyPhotoOrientation(w, h);
}

watch(
  orientationWatchKey,
  async () => {
    const sequence = ++orientationLoadSequence;
    primaryPhotoOrientation.value = null;
    hasMixedTwoPhotoOrientations.value = false;

    const firstMediaUrl = props.imagesList[0];
    if (!firstMediaUrl) return;

    const secondMediaUrl = props.imagesList[1];
    const urlsToLoad = props.imagesList.length === 2 && secondMediaUrl ? [firstMediaUrl, secondMediaUrl] : [firstMediaUrl];
    try {
      const orientations = await Promise.all(urlsToLoad.map(url => loadPhotoOrientation(url)));
      if (sequence !== orientationLoadSequence) return;
      primaryPhotoOrientation.value = orientations[0];
      if (orientations.length === 2) {
        hasMixedTwoPhotoOrientations.value = orientations[0] !== orientations[1];
      }
    } catch {
      // Keep the count-only fallback layout when thumbnail dimensions cannot be loaded.
    }
  },
  { immediate: true }
);

function getReasonForMedia(mediaUrl: string): string | null {
  if (!props.debugData) return null;
  // Show the reason for the image selection in debug mode
  // This makes it much easier to tell if the off-by-1 issue occurred
  const selected = props.debugData?.excludeFromIndex?.debugInfo?.imagesSelected || [];
  const match = selected.find((item: any) => item.filename === mediaUrl);

  if (match) return `${match.index}: ${match.reason}`;
  return null;
}
</script>

<style scoped lang="scss">
@mixin placeholderElement {
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.1);
}

@mixin orientationGridTiles {
  overflow: hidden;

  .photos-grid__photo-wrap {
    width: auto;
    aspect-ratio: auto;
    max-height: none;
    margin: 0;
    min-width: 0;
    min-height: 0;
  }

  .photos-grid__photo {
    min-width: 0;
    min-height: 0;
  }
}

// Photos grid ================================================================
.photos-grid {
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  list-style: none;

  &--1-photo-layout {
    .photos-grid__photo-wrap {
      width: 100%;
      aspect-ratio: 100/66;
    }
  }

  &--2-photos-layout {
    .photos-grid__photo-wrap {
      width: calc(50% - 1px);
      margin-right: 2px;
      aspect-ratio: 1/1;
    }
    .photos-grid__photo-wrap:last-child {
      margin-right: 0;
    }
  }

  &--3-photos-layout {
    .photos-grid__photo-wrap:nth-child(1) {
      width: 100%;
      aspect-ratio: 2/1;
      margin-bottom: 4px;
    }
    .photos-grid__photo-wrap:nth-child(2) {
      width: calc(50% - 2px);
      max-height: 150px;
      margin-right: 4px;
    }
    .photos-grid__photo-wrap:nth-child(3) {
      width: calc(50% - 2px);
      max-height: 150px;
    }
  }

  &--4-photos-layout {
    .photos-grid__photo-wrap:nth-child(1) {
      width: 100%;
      aspect-ratio: 100/50;
      margin-bottom: 4px;
    }
    .photos-grid__photo-wrap:nth-child(2),
    .photos-grid__photo-wrap:nth-child(3) {
      aspect-ratio: 1/1;
      width: calc(33.3% - 3px);
      margin-right: 4px;
    }
    .photos-grid__photo-wrap:nth-child(4) {
      aspect-ratio: 1/1;
      width: calc(33.3% - 2px);
    }
  }

  &--5-photos-layout {
    .photos-grid__photo-wrap:nth-child(1) {
      width: calc(50% - 2px);
      aspect-ratio: 1/1;
      margin: 0 4px 4px 0;
    }

    .photos-grid__photo-wrap:nth-child(2) {
      width: calc(50% - 2px);
      aspect-ratio: 1/1;
      margin-bottom: 4px;
    }

    .photos-grid__photo-wrap:nth-child(3),
    .photos-grid__photo-wrap:nth-child(4) {
      aspect-ratio: 1/1;
      width: calc(33.3% - 3px);
      margin-right: 4px;
    }

    .photos-grid__photo-wrap:nth-child(5) {
      aspect-ratio: 1/1;
      width: calc(33.3% - 2px);
    }
  }

  // Orientation-aware 2–4 photo recipes. Unknown dimensions keep the count-only fallback above.
  &--2-photos-layout.photos-grid--primary-portrait {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 2px;
    @include orientationGridTiles;

    .photos-grid__photo-wrap {
      aspect-ratio: 4 / 5;
    }
  }

  &--2-photos-layout.photos-grid--primary-landscape {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
    gap: 2px;
    aspect-ratio: 1 / 1;
    @include orientationGridTiles;
  }

  &--2-photos-layout.photos-grid--primary-square {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 2px;
    @include orientationGridTiles;

    .photos-grid__photo-wrap {
      aspect-ratio: 1 / 1;
    }
  }

  &--3-photos-layout.photos-grid--primary-portrait {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
    gap: 4px;
    aspect-ratio: 1 / 1;
    @include orientationGridTiles;

    .photos-grid__photo-wrap:nth-child(1) {
      grid-row: 1 / 3;
    }
  }

  &--3-photos-layout.photos-grid--primary-landscape,
  &--3-photos-layout.photos-grid--primary-square {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 4px;
    aspect-ratio: 1 / 1;
    @include orientationGridTiles;

    .photos-grid__photo-wrap:nth-child(1) {
      grid-column: 1 / 3;
    }
  }

  &--3-photos-layout.photos-grid--primary-landscape {
    grid-template-rows: minmax(0, 2fr) minmax(0, 1fr);
  }

  &--3-photos-layout.photos-grid--primary-square {
    grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
  }

  &--4-photos-layout.photos-grid--primary-portrait {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
    gap: 4px;
    aspect-ratio: 1 / 1;
    @include orientationGridTiles;

    .photos-grid__photo-wrap:nth-child(1) {
      grid-row: 1 / 4;
    }
  }

  &--4-photos-layout.photos-grid--primary-landscape {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
    grid-template-rows: minmax(0, 2fr) minmax(0, 1fr);
    gap: 4px;
    aspect-ratio: 1 / 1;
    @include orientationGridTiles;

    .photos-grid__photo-wrap:nth-child(1) {
      grid-column: 1 / 4;
    }
  }

  &--4-photos-layout.photos-grid--primary-square {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
    gap: 4px;
    aspect-ratio: 1 / 1;
    @include orientationGridTiles;
  }

  // Mixed two-photo orientations override portrait/landscape recipes with equal squares.
  &--2-photos-layout.photos-grid--mixed-two {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-rows: none;
    gap: 2px;
    aspect-ratio: auto;
    overflow: visible;
    @include orientationGridTiles;

    .photos-grid__photo-wrap {
      aspect-ratio: 1 / 1;
    }
  }

  &__photo-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    z-index: 0;
    overflow: hidden;
  }

  &__photo {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: flex;
    justify-content: center;
    align-items: center;

    &--hidden {
      display: none;
    }

    :deep(.media-thumbnail-checkbox__video-wrap) {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    :deep(.media-thumbnail-checkbox__video) {
      display: flex;
      justify-content: center;
      align-items: center;

      a {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }
    }
  }

  &__counter-overlay {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: 1;
    color: #fff;
    font: bold 24px/24px sans-serif;
    background: rgba(0, 0, 0, 0.5);
    pointer-events: none;
  }
}

// Fb post snippet ============================================================
.fb-post-snippet {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__user-info-skeleton {
    width: calc(100% - 32px);
    margin-bottom: 13px;
  }

  &__caption-textarea-wrap {
    width: calc(100% - 32px);
    margin-bottom: 14px;

    :deep(textarea) {
      width: 100%;
      height: 140px;
      min-height: 140px;
      box-sizing: border-box;
      border: 1px #ababab solid;
      border-radius: 6px;
      color: #5b5b5b;
      font-family: sans-serif;
      transition: none !important;
    }
  }

  &__locations {
    width: calc(100% - 32px);
    margin: -4px 0 12px;
  }

  &__photos-grid {
    width: 100%;
  }

  &__screen-size {
    width: 100%;
    margin-bottom: 8px;
    color: #6a6a6a;
    font: 12px/16px sans-serif;
    text-align: center;
  }

  &__single-photo-media {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__footer-skeleton {
    width: calc(100% - 26px);
  }
}
</style>
